/**
 * One message, with its attachment if it has one.
 *
 * *** THE ATTACHMENT IS EMBEDDED, THE BYTES ARE NOT ***
 * The server sends file name, type, size and availability with every
 * message, so a page renders without follow-up requests. Only opening a
 * file costs a round trip — and that one re-authorizes the caller, because
 * knowing an id is not access.
 */

import { useEffect, useRef, useState } from 'react'
import { ApiError, fetchAttachmentBlobUrl } from '@/lib/api'
import type { ChatMessage } from '@/types/api'
import styles from './MessagesPage.module.css'

function sizeLabel(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function clock(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

export function MessageBubble({ message }: { message: ChatMessage }) {
  const att = message.attachment

  const [url, setUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  /*
    *** A BLOB URL MUST BE REVOKED ***
    Each one pins its bytes in memory until the tab closes. A long
    conversation with twenty images would hold all twenty.
  */
  const urlRef = useRef<string | null>(null)
  useEffect(() => {
    urlRef.current = url
  }, [url])
  useEffect(
    () => () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current)
    },
    [],
  )

  const load = async () => {
    if (!att || url || loading) return
    setLoading(true)
    setError(null)
    try {
      setUrl(await fetchAttachmentBlobUrl(att.attachment_id))
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : 'Could not open this file.',
      )
    } finally {
      setLoading(false)
    }
  }

  /*
    *** IMAGES LOAD THEMSELVES, FILES DO NOT ***
    A thumbnail that needs a click is not a thumbnail. A PDF is opened
    deliberately, and fetching every one on render would download a
    conversation's worth of documents nobody asked for.
  */
  useEffect(() => {
    if (att?.is_image && att.is_available && !url && !loading && !error) {
      void load()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [att?.attachment_id, att?.is_available])

  return (
    <article
      className={`${styles.bubble} ${message.is_mine ? styles.mine : styles.theirs}`}
    >
      {att && (
        <div className={styles.attachment}>
          {/*
            `is_available` is false once deleted, and while a virus scan is
            outstanding. A placeholder beats a broken download.
          */}
          {!att.is_available ? (
            <span className={styles.attachNote}>
              {att.scan_status === 'PENDING'
                ? 'Checking this file…'
                : 'This file is no longer available.'}
            </span>
          ) : att.is_image ? (
            url ? (
              <a href={url} target="_blank" rel="noopener noreferrer">
                <img
                  className={styles.image}
                  src={url}
                  alt={att.file_name}
                  loading="lazy"
                />
              </a>
            ) : (
              <span className={styles.attachNote}>
                {error ?? 'Loading image…'}
              </span>
            )
          ) : (
            <div className={styles.file}>
              <span className={styles.fileIcon} aria-hidden="true">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <path d="M14 2v6h6" />
                </svg>
              </span>
              <span className={styles.fileText}>
                <span className={styles.fileName}>{att.file_name}</span>
                <span className={styles.fileSize}>
                  {sizeLabel(att.size_bytes)}
                </span>
              </span>
              {url ? (
                <a
                  className={styles.fileOpen}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open
                </a>
              ) : (
                <button
                  type="button"
                  className={styles.fileOpen}
                  onClick={load}
                  disabled={loading}
                >
                  {loading ? 'Opening…' : 'Open'}
                </button>
              )}
            </div>
          )}
          {error && !att.is_image && (
            <span className={styles.attachNote}>{error}</span>
          )}
        </div>
      )}

      {message.body && <p className={styles.body}>{message.body}</p>}

      <span className={styles.meta}>
        {clock(message.sent_at)}
        {/* Only on your own messages — "read" on someone else's says
            nothing you did not already know. */}
        {message.is_mine && message.read_at && (
          <span className={styles.readMark} title="Read">
            {' '}
            ✓✓
          </span>
        )}
      </span>
    </article>
  )
}
